import { ResponsiveTable, ResponsiveCell } from "../components/ResponsiveTable";
import { useCallback, useState } from "react";
import type { Subscriber } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { ConfirmDialog } from "../components/ConfirmDialog";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Build and download a CSV of the current subscribers (client-side, no API call). */
function exportCsv(subscribers: Subscriber[]): void {
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const rows = [
    ["email", "source", "createdAt"],
    ...subscribers.map((s) => [s.email, s.source, s.createdAt]),
  ];
  const csv = rows.map((r) => r.map(escape).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function Subscribers() {
  const api = useApi();
  const load = useCallback(() => api.listSubscribers(), [api]);
  const { data, loading, error, reload, setData } = useAsync<Subscriber[]>(load, [load]);

  const [toDelete, setToDelete] = useState<Subscriber | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    setActionError(undefined);
    try {
      await api.deleteSubscriber(toDelete.id);
      const deletedId = toDelete.id;
      setData((prev) => (prev ? prev.filter((s) => s.id !== deletedId) : prev));
      setToDelete(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="Loading subscribers…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Subscribers</h1>
          <p className="page__subtitle">
            {data.length} {data.length === 1 ? "subscriber" : "subscribers"} on the mailing list
          </p>
        </div>
        <div className="page__actions">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => exportCsv(data)}
            disabled={data.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      {actionError ? <ErrorBanner title="Action failed" message={actionError} /> : null}

      {data.length === 0 ? (
        <div className="empty-state">
          <p>No subscribers yet. Shoppers can opt in from the storefront cart.</p>
        </div>
      ) : (
        <ResponsiveTable label="Subscribers">
          <thead role="rowgroup">
            <tr role="row">
              <th scope="col" role="columnheader">Email</th>
              <th scope="col" role="columnheader">Source</th>
              <th scope="col" role="columnheader">Subscribed</th>
              <th scope="col" role="columnheader" className="table__actions-col">Actions</th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {data.map((subscriber) => (
              <tr role="row" key={subscriber.id}>
                <ResponsiveCell label="Email">{subscriber.email}</ResponsiveCell>
                <ResponsiveCell label="Source" className="muted">{subscriber.source}</ResponsiveCell>
                <ResponsiveCell label="Subscribed" className="muted">{formatDate(subscriber.createdAt)}</ResponsiveCell>
                <ResponsiveCell label="Actions" className="table__actions">
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    onClick={() => setToDelete(subscriber)}
                  >
                    Delete
                  </button>
                </ResponsiveCell>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}

      {toDelete ? (
        <ConfirmDialog
          title="Delete subscriber"
          message={`Remove “${toDelete.email}” from the mailing list? This deletes them from both the CMS and the email contact list.`}
          confirmLabel="Delete"
          danger
          busy={busy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setToDelete(null)}
        />
      ) : null}
    </div>
  );
}
